import { storage } from "../../../boot/firebase";
import { updateDoc } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
  uploadString,
} from "firebase/storage";
import { fetchBlob } from "../../../util/misc";
import FileRef from "../../../classes/FileRef";
import { normalizeUploadImageFile } from "../../../util/imageUpload";
import { differenceBy, get } from "lodash";
import asyncPool from "tiny-async-pool";
import { uid } from "quasar";

const MAX_CONCURRENT = 5;

// Documents
export const SAVE_DOCUMENTS = async (
  { state },
  {
    batch,
    media,
    parentData,
    existingData,
    parentRef,
    fieldPath,
    folderPath,
    isSingle,
  }
) => {
  const actions = [];
  const folderStorageRef = ref(storage, folderPath);

  let documents = media || get(parentData, fieldPath) || (isSingle ? null : []);

  actions.delete = async (document) => {
    try {
      const docStorageRef = ref(folderStorageRef, document.file.name);
      await deleteObject(docStorageRef);
    } catch (error) {
      console.error(error);
    }
  };
  actions.upload = async (document) => {
    const normalizedFile = await normalizeUploadImageFile(document.file.toSave);
    if (normalizedFile !== document.file.toSave) {
      document.file = {
        ...document.file,
        ...new FileRef({ toSave: normalizedFile }).data,
      };
    }

    const docStorageRef = ref(folderStorageRef, document.file.name);
    await uploadBytes(docStorageRef, normalizedFile);
    document.file.toSave = null;
    document.file.url = await getDownloadURL(docStorageRef);
    return document;
  };
  actions.run = async () => {
    if (actions.length) {
      return asyncPool(MAX_CONCURRENT, actions, (item) =>
        actions[item.action](item.document)
      );
    }
  };

  const hasFile = (doc) => doc && doc.file && doc.file.name;
  let newDocuments;

  if (isSingle) {
    if (hasFile(documents)) {
      newDocuments = [documents].filter((d) => d.file.toSave);
    } else {
      documents = null;
      newDocuments = [];
    }
  } else {
    documents = documents.filter(hasFile);
    newDocuments = documents.filter((d) => d.file.toSave);
  }

  // Find and delete removed files
  if (existingData) {
    let oldDocuments = get(existingData, fieldPath);
    if (oldDocuments) {
      if (isSingle) {
        if (
          hasFile(oldDocuments) &&
          (!documents || oldDocuments.name !== documents.name)
        ) {
          actions.push({
            action: "delete",
            document: oldDocuments,
          });
        }
      } else {
        oldDocuments = oldDocuments.filter(hasFile);
        if (oldDocuments.length) {
          actions.push(
            ...differenceBy(oldDocuments, documents, (d) => d.file.name).map(
              (document) => ({
                action: "delete",
                document,
              })
            )
          );
        }
      }
    }
  }

  // Upload new files
  actions.push(
    ...newDocuments.map((document) => ({
      action: "upload",
      document,
    }))
  );

  await actions.run().then(() => {
    if (parentRef && actions.length) {
      const changes = {
        modifiedBy: state.user.id,
        [fieldPath]: documents,
      };
      if (batch) {
        return batch.update(parentRef, changes);
      } else {
        return updateDoc(parentRef, changes);
      }
    }
  });

  return documents;
};

// Images
export const SAVE_IMAGES = async (
  { state },
  {
    batch,
    parentData,
    existingData,
    parentRef,
    fieldPath,
    folderPath,
    isSingle,
  }
) => {
  const actions = [];
  const folderStorageRef = ref(storage, folderPath);

  let images = get(parentData, fieldPath) || (isSingle ? null : []);

  actions.delete = async (image) => {
    try {
      const imageStorageRef = ref(folderStorageRef, image.id);
      await deleteObject(imageStorageRef);
    } catch (error) {
      console.error(error);
    }
  };
  actions.upload = async (image) => {
    const imageStorageRef = ref(folderStorageRef, image.id);

    // Upload local image
    if (!image.id) {
      // Create image id if none exists
      image.id = uid().slice(-12) + ".png";
    }

    if (image.src.startsWith("blob:")) {
      // If blob URL, get the blob first
      let blob = await fetchBlob(image.src);
      await uploadBytes(imageStorageRef, blob);
    } else {
      // If data URL, upload directly
      await uploadString(imageStorageRef, image.src, "data_url");
    }
    image.src = await getDownloadURL(imageStorageRef);
  };
  actions.run = async () => {
    if (actions.length) {
      return asyncPool(MAX_CONCURRENT, actions, (item) =>
        actions[item.action](item.image)
      );
    }
  };

  const hasFile = (img) => img && img.src && img.id;
  const hasNewFile = (img) => img && img.src && /^(data|blob):/.test(img.src);
  let newImages;

  if (isSingle) {
    if (hasNewFile(images)) {
      newImages = [images];
    } else {
      images = null;
      newImages = [];
    }
  } else {
    newImages = images.filter(hasNewFile);
  }

  // Find and delete removed files
  if (existingData) {
    let oldImages = get(existingData, fieldPath);
    if (oldImages) {
      if (isSingle) {
        if (hasFile(oldImages) && (!images || oldImages.id !== images.id)) {
          actions.push({
            action: "delete",
            image: oldImages,
          });
        }
      } else {
        oldImages = oldImages.filter(hasFile);
        if (oldImages.length) {
          actions.push(
            ...differenceBy(oldImages, images, (img) => img.id).map(
              (image) => ({
                action: "delete",
                image,
              })
            )
          );
        }
      }
    }
  }

  // Upload new files
  actions.push(
    ...newImages.map((image) => ({
      action: "upload",
      image,
    }))
  );

  return parentRef && actions.length
    ? actions.run().then(() => {
        const changes = {
          modifiedBy: state.user.id,
          [fieldPath]: images,
        };
        if (batch) {
          return batch.update(parentRef, changes);
        } else {
          return updateDoc(parentRef, changes);
        }
      })
    : actions.run();
};
